import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Star, Info, Loader2 } from 'lucide-react'
import { useResource, useUpdateResource } from '@/shared/hooks/useResources'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'

export default function ResourcePriority() {
  const { resourceId } = useParams<{ resourceId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading } = useTelegram()
  const { data: resource, isLoading } = useResource(resourceId)
  const updateResource = useUpdateResource()

  const [priorityEnabled, setPriorityEnabled] = useState(false)
  const [priorityPrice, setPriorityPrice] = useState<number>(5)
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<string>('')

  const handleSaveRef = useRef<() => Promise<void>>()

  // Initialize from resource data
  useEffect(() => {
    if (resource && !isInitialized) {
      if (resource.priority_price && resource.priority_price > 0) {
        setPriorityEnabled(true)
        setPriorityPrice(resource.priority_price)
      } else {
        setPriorityEnabled(false)
      }
      setIsInitialized(true)
    }
  }, [resource, isInitialized])

  const handleSave = async () => {
    if (!resource) return

    if (priorityEnabled && (priorityPrice < 1 || priorityPrice > 100)) {
      setError('Цена приоритета должна быть от 1 до 100 рублей')
      hapticNotification('error')
      return
    }

    setError('')
    hapticFeedback('medium')
    setMainButtonLoading(true)

    try {
      await updateResource.mutateAsync({
        resourceId: resource.id,
        data: {
          priority_price: priorityEnabled ? priorityPrice : 0,
        },
      })
      hapticNotification('success')

      setMainButtonLoading(false)
      hideMainButton()
      window.scrollTo(0, 0)
      navigate(`/traffic/${resourceId}`, {
        replace: true,
        state: { successMessage: 'Ресурс успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update priority:', error)
      setError('Ошибка при сохранении приоритета')
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  handleSaveRef.current = handleSave

  // Auto-manage back button
  useBackButton()

  useEffect(() => {
    if (!isReady) return

    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    setMainButtonLoading(false)
    showMainButton('Сохранить приоритет', onSave, '#5288c1', '#ffffff')

    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, setMainButtonLoading])

  if (isLoading || !resource) {
    return (
      <div className="min-h-screen bg-tg-bg flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-tg-button" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-yellow-500/10 rounded-xl flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Приоритет ресурса</h1>
              <p className="text-sm text-tg-hint mt-1">{resource.name}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-4">
        {/* Unified Priority Card */}
        <div
          className="rounded-xl p-4 space-y-4 border-2 transition-all"
          style={{
            borderColor: '#f59e0b33',
            backgroundColor: '#f59e0b0d'
          }}
        >
          {/* Toggle Section */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#f59e0b1a' }}
              >
                <Star className="w-5 h-5 text-[#f59e0b]" />
              </div>
              <div>
                <div className="font-semibold text-tg-text">Приоритетная выдача</div>
                <div className="text-xs text-tg-hint">Ресурс будет показываться первым</div>
              </div>
            </div>
            <button
              onClick={() => {
                hapticFeedback('light')
                setPriorityEnabled(!priorityEnabled)
                setError('')
              }}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                priorityEnabled ? 'bg-[#059669]' : 'bg-tg-bg'
              }`}
            >
              <div
                className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  priorityEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Price Settings - Only shown when enabled */}
          {priorityEnabled && (
            <div className="space-y-3 pt-2 border-t border-[#f59e0b]/20">
              {/* Current Price Display */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-tg-hint">Цена за день</span>
                <div className="text-xl font-bold text-[#f59e0b]">
                  {priorityPrice.toFixed(2)} ₽
                </div>
              </div>

              {/* Slider */}
              <div className="space-y-2">
                <input
                  type="range"
                  min={1}
                  max={50}
                  step={0.5}
                  value={priorityPrice}
                  onChange={(e) => {
                    setPriorityPrice(parseFloat(e.target.value))
                    setError('')
                  }}
                  onTouchStart={() => hapticFeedback('light')}
                  className="w-full h-2 bg-tg-hint/20 rounded-lg appearance-none cursor-pointer slider-thumb"
                  style={{
                    background: `linear-gradient(to right, #f59e0b 0%, #f59e0b ${((priorityPrice - 1) / (50 - 1)) * 100}%, rgba(168, 179, 207, 0.2) ${((priorityPrice - 1) / (50 - 1)) * 100}%, rgba(168, 179, 207, 0.2) 100%)`,
                    ['--slider-color' as any]: '#f59e0b'
                  }}
                />
                <div className="flex justify-between text-xs text-tg-hint">
                  <span>1₽</span>
                  <span>50₽</span>
                </div>
              </div>

              {/* Preset Buttons */}
              <div className="flex gap-1.5">
                {[5, 10, 15, 20, 30].map((val) => (
                  <button
                    key={val}
                    onClick={() => {
                      hapticFeedback('light')
                      setPriorityPrice(val)
                      setError('')
                    }}
                    className="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all"
                    style={
                      Math.abs(priorityPrice - val) < 0.01
                        ? { backgroundColor: '#f59e0b', color: '#ffffff' }
                        : { backgroundColor: '#f59e0b26', color: '#d97706' }
                    }
                  >
                    {val}₽
                  </button>
                ))}
              </div>

              {/* Number Input */}
              <div className="relative">
                <input
                  type="number"
                  min={1}
                  max={100}
                  step={0.5}
                  value={priorityPrice}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value)
                    if (!isNaN(value)) {
                      setPriorityPrice(value)
                      setError('')
                    }
                  }}
                  onFocus={() => hapticFeedback('light')}
                  className="w-full px-4 py-2.5 text-sm font-medium text-center border-2 rounded-lg focus:outline-none transition-colors bg-tg-bg text-tg-text"
                  style={{
                    borderColor: '#f59e0b33',
                    ['&:focus' as any]: { borderColor: '#f59e0b' }
                  }}
                  placeholder="Введите цену"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-tg-hint">
                  ₽/день
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-600/10 border-2 border-red-600/20 rounded-xl">
            <p className="text-sm text-red-600 text-center font-medium">{error}</p>
          </div>
        )}

        {/* Info Card */}
        <div className="bg-tg-secondary-bg rounded-xl p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-tg-button flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-tg-text mb-1.5">Подробнее о приоритете</h3>
              <p className="text-sm text-tg-hint leading-relaxed">
                Здесь будет подробное описание.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
